import { SelectProps } from 'antd';
import { get } from 'lodash-es';
import { useCallback } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import FieldWithLabel from '../../../common/components/Fields/FieldWithLabel';
import { FieldProps } from '../../../common/components/Fields/types';
import AsyncSelect from '../../../common/components/Input/AsyncSelect';
import {
  MarketOptionsQuery,
  useMarketOptionsLazyQuery,
} from '../graphql/markets.generated';

export type MarketOption =
  MarketOptionsQuery['adminMarkets']['edges']['0']['node'];

export type SelectFieldProps<TFieldValues extends FieldValues> = SelectProps &
  FieldProps & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    initialOption?: { value: number; label: string };
    sellerId?: number | null;
  };

function MarketSelectField<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  control,
  hideError,
  onChange,
  sellerId,
  ...props
}: SelectFieldProps<TFieldValues>) {
  const [fetchMarket] = useMarketOptionsLazyQuery({ fetchPolicy: 'no-cache' });
  const fetchItems = useCallback(
    async (name: string) => {
      const { data } = await fetchMarket({
        variables: {
          filter: { name, sellerId: sellerId ? sellerId : undefined },
          pagination: { skip: 0, take: 20 },
        },
      });
      return (
        data?.adminMarkets.edges.map(({ node }) => ({
          label: node.name,
          value: node.id,
          data: node,
        })) || []
      );
    },
    [fetchMarket, sellerId]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;
        const status = !hideError && errorMessage ? 'error' : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <AsyncSelect
              initialLoad
              initialLoadTrigger={sellerId}
              className={'w-full'}
              fetchItems={fetchItems}
              status={status}
              {...field}
              onChange={(v, option) => {
                field.onChange(v);

                onChange?.(v, option);
              }}
              {...props}
            />

            {!hideError && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}

export default MarketSelectField;
